// Canonical address utilities.
//
// Background: address records in Firestore carry both a single combined
// `address` string ("13407 Seward St, Omaha, NE 68154") and structured
// components (`address1`, `address2`, `city`, `state`, `zip`). The combined
// string is used as a join key across collections — `users.address`,
// `invitations.address`, `householdInvites.address` — and is also read
// directly by older iOS/Android builds.
//
// In the past we hit a "duplicate household" bug because two write paths
// produced slightly different combined strings for the same physical address
// ("13505 Parker Street" vs "13505 Parker Street, Omaha, NE 68154"), and the
// lookup logic treated them as separate households. This helper exists so
// every place that creates or updates an address-bearing record can derive
// the combined string from the structured fields the same way, eliminating
// drift.
//
// TODO(canonical-address): Roll this helper into every call site that writes
// any of these fields. Targets:
//
//   Web (this repo):
//     - src/pages/ManageNeighborsSection.jsx
//         handleAddAddresses() — when creating address subcollection docs
//         The address1/address2/city/state/zip auto-sync at the top of
//         loadAddresses() (already correct, but worth using the helper).
//     - src/pages/SignUpPage.jsx — when writing the new user's address
//     - src/pages/AdminRegistrationPage.jsx — same
//     - src/pages/ProfilePage.jsx — when a resident edits their address
//     - Anywhere else `address:` is set in a Firestore write.
//
//   iOS (NeighborHub/):
//     - AdminViewModel.swift: createHousehold, generateInvitation,
//       generateAndAddInvitation, bulkCreateInvitations — each builds the
//       address string from components today; replace with a Swift port of
//       this helper for parity.
//     - RegistrationView.swift — writes user.address on sign-up
//
//   Android (NeighborTopia/):
//     - FirestoreService.kt: createInvitation, the vacant-household path
//       inside markHouseholdInactive, any user-create or user-update path
//       that touches `address`.
//
// Bundle the call-site swap with the next mobile release so the testing
// cost is paid once. After it ships, run a one-time backfill Cloud Function
// that rewrites `address` on every doc in addresses/users/invitations/
// householdInvites using these structured fields — that heals any historical
// drift permanently.

/**
 * Build the canonical combined address string from structured components.
 * Returns "" when there isn't enough data to form a useful string.
 *
 * Output format (matches what the iOS/Android apps and existing Firestore
 * data use):
 *   "<address1>[ <address2>], <city>, <state> <zip>"
 *
 * Trims whitespace on every component, skips empty parts gracefully, and
 * collapses inner whitespace so two callers that pass " 13407   Seward  St "
 * and "13407 Seward St" produce identical output. This is what prevents the
 * "duplicate household" drift bug.
 */
export function buildCanonicalAddress({
  address1 = '',
  address2 = '',
  city = '',
  state = '',
  zip = '',
} = {}) {
  const normalize = (s) => String(s || '').trim().replace(/\s+/g, ' ')

  const a1 = normalize(address1)
  const a2 = normalize(address2)
  const c = normalize(city)
  const s = normalize(state).toUpperCase()
  const z = normalize(zip)

  // Need at least a street line to produce something meaningful.
  if (!a1) return ''

  const streetLine = a2 ? `${a1} ${a2}` : a1
  const cityStateZip = [c, s && z ? `${s} ${z}` : (s || z)]
    .filter(Boolean)
    .join(', ')

  return cityStateZip ? `${streetLine}, ${cityStateZip}` : streetLine
}

/**
 * Compare two address strings for "are these the same physical address"
 * after normalizing whitespace and case. Useful when looking up an existing
 * doc by address but tolerating drift in older records.
 */
export function addressesMatch(a, b) {
  const norm = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase()
  return norm(a) === norm(b)
}
